/**
 * On-screen thumbstick for touch devices.
 *
 * Tap-to-walk already works on a phone, but it can't express "hold this
 * direction" — and a farming game where you can only teleport-by-tap feels like
 * a menu, not a world. This gives touch the same continuous control the keyboard
 * has, reporting a normalised vector through the same `setKeys` path so the
 * player state machine never learns there are two kinds of input.
 *
 * DOM rather than Pixi: it must sit above the canvas, ignore the camera's pan and
 * zoom entirely, and respect the safe area on a notched phone. All three are free
 * in CSS and fiddly in the scene graph.
 */

export interface Joystick {
  el: HTMLElement;
  destroy(): void;
}

const SIZE = 120; // outer ring, px
const KNOB = 52;
const DEAD = 0.18; // fraction of the radius that reads as no input

/** True for devices whose primary input is touch — a phone or tablet, not a laptop trackpad. */
export function isTouchDevice(): boolean {
  // DEV escape hatch: `?touch=1` forces the mobile branch so the phone layout can
  // be measured in a desktop browser.
  //
  // It is deliberately NOT sticky. An earlier version latched the override into
  // sessionStorage so it survived the app's history.pushState to /play — and that
  // is exactly what made it leak: once any measurement run set it, the desktop
  // browser rendered the thumbstick and the phone HUD on every load afterwards,
  // with no way to turn it off short of clearing storage. A per-navigation read is
  // enough for a harness that controls its own URLs, and it cannot outlive the page.
  //
  // Any key left behind by the old sticky version is cleared here, so a browser
  // already poisoned by it recovers on the next load.
  if (import.meta.env.DEV && typeof location !== 'undefined') {
    try {
      sessionStorage.removeItem('tb-force-touch');
      if (new URLSearchParams(location.search).has('touch')) return true;
    } catch { /* storage disabled → fall through to the real device test */ }
  }
  return typeof matchMedia === 'function' && matchMedia('(hover: none) and (pointer: coarse)').matches;
}

/**
 * @param onMove normalised (-1..1) direction, already deadzoned. (0,0) = released.
 */
export function createJoystick(
  parent: HTMLElement,
  onMove: (vx: number, vy: number) => void,
  opts: { docked?: boolean } = {},
): Joystick {
  const base = document.createElement('div');
  base.setAttribute('aria-hidden', 'true'); // decorative: keyboard users have WASD
  // `docked`: the farm lays its bottom row out as ONE flex band (stick · hotbar ·
  // action) so the three can never overlap. There the parent owns the position and
  // the stick is just a flex item. The arena still self-positions in the corner.
  const anchor = opts.docked
    ? 'position:relative;flex:none;'
    : `position:absolute;left:max(18px,env(safe-area-inset-left));bottom:max(90px,env(safe-area-inset-bottom));`;
  base.style.cssText = `${anchor}
    width:${SIZE}px;height:${SIZE}px;border-radius:50%;z-index:30;touch-action:none;
    background:radial-gradient(circle,rgba(0,0,0,.28),rgba(0,0,0,.14));border:2px solid rgba(255,255,255,.22);
    opacity:.5;transition:opacity .15s`;

  const knob = document.createElement('div');
  knob.style.cssText = `position:absolute;left:50%;top:50%;width:${KNOB}px;height:${KNOB}px;margin:${-KNOB / 2}px 0 0 ${-KNOB / 2}px;
    border-radius:50%;background:rgba(255,255,255,.75);border:2px solid rgba(0,0,0,.3);pointer-events:none`;
  base.appendChild(knob);
  parent.appendChild(base);

  const R = SIZE / 2 - KNOB / 4;
  let id: number | null = null;

  const place = (dx: number, dy: number) => {
    knob.style.transform = `translate(${dx}px, ${dy}px)`;
  };

  const handle = (e: PointerEvent) => {
    const r = base.getBoundingClientRect();
    let dx = e.clientX - (r.left + r.width / 2);
    let dy = e.clientY - (r.top + r.height / 2);
    const dist = Math.hypot(dx, dy);
    // Clamp the knob to the ring, but keep the *direction* — dragging past the
    // edge should keep steering, not stall at the boundary.
    if (dist > R) {
      dx = (dx / dist) * R;
      dy = (dy / dist) * R;
    }
    place(dx, dy);
    const nx = dx / R;
    const ny = dy / R;
    const mag = Math.hypot(nx, ny);
    if (mag < DEAD) onMove(0, 0);
    else onMove(nx, ny);
  };

  const onDown = (e: PointerEvent) => {
    id = e.pointerId;
    base.setPointerCapture(e.pointerId);
    base.style.opacity = '.85';
    handle(e);
    e.preventDefault();
    e.stopPropagation(); // don't let the camera read this as a pan
  };
  const onMoveEvt = (e: PointerEvent) => {
    if (e.pointerId !== id) return;
    handle(e);
    e.preventDefault();
    e.stopPropagation();
  };
  const onUp = (e: PointerEvent) => {
    if (e.pointerId !== id) return;
    id = null;
    base.style.opacity = '.5';
    place(0, 0);
    onMove(0, 0);
    e.preventDefault();
    e.stopPropagation();
  };

  base.addEventListener('pointerdown', onDown);
  base.addEventListener('pointermove', onMoveEvt);
  base.addEventListener('pointerup', onUp);
  base.addEventListener('pointercancel', onUp);

  return {
    el: base,
    destroy() {
      base.removeEventListener('pointerdown', onDown);
      base.removeEventListener('pointermove', onMoveEvt);
      base.removeEventListener('pointerup', onUp);
      base.removeEventListener('pointercancel', onUp);
      base.remove();
    },
  };
}
